/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import React, { useEffect, useRef, useCallback, useState } from 'react';
import { Terminal } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import '@xterm/xterm/css/xterm.css';
import { ArrowPathIcon } from '@heroicons/react/24/outline';

interface Props {
  wsUrl: string;
  title?: string;
}

interface TerminalMessage {
  type: 'input' | 'output' | 'resize' | 'connected' | 'error';
  data?: string;
  cols?: number;
  rows?: number;
  error?: string;
}

const AssignedTerminal: React.FC<Props> = ({ wsUrl, title = 'Terminal' }) => {
  const terminalRef = useRef<HTMLDivElement>(null);
  const terminalInstanceRef = useRef<Terminal | null>(null);
  const fitAddonRef = useRef<FitAddon | null>(null);
  const wsRef = useRef<WebSocket | null>(null);

  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sendResize = useCallback((cols: number, rows: number) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      const message: TerminalMessage = { type: 'resize', cols, rows };
      wsRef.current.send(JSON.stringify(message));
    }
  }, []);

  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    setIsConnecting(true);
    setError(null);

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        setIsConnected(true);
        setIsConnecting(false);
        setError(null);
        if (terminalInstanceRef.current) {
          sendResize(terminalInstanceRef.current.cols, terminalInstanceRef.current.rows);
        }
      };

      ws.onmessage = (event) => {
        try {
          const message: TerminalMessage = JSON.parse(event.data);
          switch (message.type) {
            case 'connected':
              terminalInstanceRef.current?.write('\r\n\x1b[32mConnected to container\x1b[0m\r\n\r\n');
              break;
            case 'output':
              if (message.data && terminalInstanceRef.current) {
                terminalInstanceRef.current.write(message.data);
              }
              break;
            case 'error':
              setError(message.error || 'Terminal error');
              terminalInstanceRef.current?.write(
                `\r\n\x1b[31mError: ${message.error}\x1b[0m\r\n`
              );
              break;
            default:
              break;
          }
        } catch {
          // If not JSON, treat as raw output
          if (terminalInstanceRef.current) {
            terminalInstanceRef.current.write(event.data);
          }
        }
      };

      ws.onerror = () => {
        setError('WebSocket connection error');
        setIsConnected(false);
        setIsConnecting(false);
      };

      ws.onclose = () => {
        setIsConnected(false);
        setIsConnecting(false);
        terminalInstanceRef.current?.write('\r\n\x1b[33mDisconnected\x1b[0m\r\n');
      };
    } catch {
      setError('Failed to connect to terminal');
      setIsConnecting(false);
    }
  }, [wsUrl, sendResize]);

  const disconnect = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setIsConnected(false);
  }, []);

  useEffect(() => {
    if (!terminalRef.current || terminalInstanceRef.current) return;

    const terminal = new Terminal({
      cursorBlink: true,
      fontSize: 15,
      fontFamily: 'Menlo, Monaco, "Courier New", monospace',
      theme: {
        background: '#0d0d0d',
        foreground: '#d4d4d4',
        cursor: '#ffffff',
        cursorAccent: '#0d0d0d',
        selectionBackground: '#264f78',
        black: '#000000',
        red: '#cd3131',
        green: '#0dbc79',
        yellow: '#e5e510',
        blue: '#2472c8',
        magenta: '#bc3fbc',
        cyan: '#11a8cd',
        white: '#e5e5e5',
        brightBlack: '#666666',
        brightRed: '#f14c4c',
        brightGreen: '#23d18b',
        brightYellow: '#f5f543',
        brightBlue: '#3b8eea',
        brightMagenta: '#d670d6',
        brightCyan: '#29b8db',
        brightWhite: '#ffffff',
      },
      scrollback: 10000,
      allowProposedApi: true,
    });

    const fitAddon = new FitAddon();
    terminal.loadAddon(fitAddon);
    terminal.open(terminalRef.current);
    fitAddon.fit();

    terminalInstanceRef.current = terminal;
    fitAddonRef.current = fitAddon;

    terminal.onData((data) => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        const message: TerminalMessage = { type: 'input', data };
        wsRef.current.send(JSON.stringify(message));
      }
    });

    terminal.onResize(({ cols, rows }) => {
      sendResize(cols, rows);
    });

    terminal.write(`\x1b[1;32mPodstack Terminal\x1b[0m - ${title}\r\n`);
    terminal.write('Connecting...\r\n\r\n');

    connect();

    const handleResize = () => {
      if (fitAddonRef.current) {
        fitAddonRef.current.fit();
      }
    };
    window.addEventListener('resize', handleResize);
    terminal.focus();

    return () => {
      window.removeEventListener('resize', handleResize);
      disconnect();
      terminal.dispose();
      terminalInstanceRef.current = null;
      fitAddonRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleReconnect = () => {
    disconnect();
    terminalInstanceRef.current?.clear();
    terminalInstanceRef.current?.write('Reconnecting...\r\n\r\n');
    setTimeout(connect, 500);
  };

  return (
    <div className="flex flex-col rounded overflow-hidden border border-gray-700" style={{ height: '400px' }}>
      {/* Header Bar */}
      <div className="flex items-center justify-between px-4 py-2 bg-gray-900 border-b border-gray-800">
        <div className="flex items-center gap-3">
          <span className="text-white font-medium text-sm">{title}</span>
          {isConnected ? (
            <span className="flex items-center gap-1 text-xs text-green-500 bg-green-500/20 px-2 py-0.5 rounded-full">
              <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
              Connected
            </span>
          ) : isConnecting ? (
            <span className="flex items-center gap-1 text-xs text-yellow-500 bg-yellow-500/20 px-2 py-0.5 rounded-full">
              <ArrowPathIcon className="w-3 h-3 animate-spin" />
              Connecting
            </span>
          ) : (
            <span className="flex items-center gap-1 text-xs text-red-500 bg-red-500/20 px-2 py-0.5 rounded-full">
              <span className="w-1.5 h-1.5 bg-red-500 rounded-full" />
              Disconnected
            </span>
          )}
        </div>
        <button
          onClick={handleReconnect}
          className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-800 rounded"
          title="Reconnect"
        >
          <ArrowPathIcon className="w-4 h-4" />
        </button>
      </div>

      {/* Terminal */}
      <div
        ref={terminalRef}
        className="flex-1"
        style={{ backgroundColor: '#0d0d0d' }}
      />

      {/* Error overlay */}
      {error && !isConnected && (
        <div className="absolute bottom-4 left-4 right-4 p-3 bg-red-500/20 border border-red-500/30 rounded-lg">
          <p className="text-sm text-red-400">{error}</p>
          <button
            onClick={handleReconnect}
            className="mt-2 text-xs text-red-400 hover:text-red-300 underline"
          >
            Try reconnecting
          </button>
        </div>
      )}
    </div>
  );
};

export default AssignedTerminal;
